import {
  takeLatest,
  put,
  select,
  call,
  takeEvery,
  fork,
  take,
  delay,
  all,
} from 'redux-saga/effects';
import allSettled from 'promise.allsettled';
import differenceBy from 'lodash.differenceby';
import {
  Timestamp,
  doc,
  collection,
  getDoc,
  query,
  where,
  addDoc,
  updateDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

import { db, storage } from 'firebaseConfig';

import { createDocumentChangeChannel, convertDownloadURLToGS } from 'utils';

import { showSnackbar, hideSnackbar } from 'modules/App/store/actions';
import { pushNotification } from 'modules/Users/store/actions';

import axios from 'axios';
import { actions, teamsSuccess } from './actions';

import * as selectors from './selectors';

function* teamsChannel(snapshot) {
  const user = yield select(selectors.user);
  let unknownUsers = [];
  let allTeams = [];

  allTeams = snapshot.empty
    ? []
    : snapshot.docs.map((snap) => ({ ...snap.data(), id: snap.id }));

  if (allTeams.length > 0) {
    allTeams.forEach((team) => {
      team.users.forEach((u) => {
        if (u.uid !== user.uid) unknownUsers.push(u.uid);
      });
    });

    unknownUsers = Array.from(
      new Set(unknownUsers.filter((i) => Boolean(i))),
    ).map((uid) =>
      getDoc(doc(db, 'users', uid)).then((snap) =>
        snap.exists() ? { ...snap.data(), uid } : uid,
      ),
    );

    unknownUsers = yield allSettled(unknownUsers);
    unknownUsers = unknownUsers
      .map(({ value }) => value)
      .filter((u) => typeof u === 'object');

    allTeams = allTeams.map((team) => ({
      ...team,
      users: team.users.map((u) => {
        if (u.uid === user.uid) return { ...user, ...u };

        return { ...unknownUsers.find(({ uid }) => uid === u.uid), ...u };
      }),
    }));
  }

  yield put(teamsSuccess(allTeams));
}

function* cancelTeamsChannel(channel) {
  yield take(actions.CANCEL_TEAMS_LISTENER);
  channel.close();
}

function* teamsRequestSaga() {
  const user = yield select(selectors.user);
  const conditions = [
    { uid: user.uid, access: 'member' },
    { uid: user.uid, access: 'manager' },
  ];

  const teamsColChannel = yield call(
    createDocumentChangeChannel,
    query(
      collection(db, 'teams'),
      where('users', 'array-contains-any', conditions),
    ),
  );

  yield takeEvery(teamsColChannel, teamsChannel);
  yield fork(cancelTeamsChannel, teamsColChannel);
}

export function* watchTeamRequest() {
  yield takeLatest(actions.TEAMS_REQUEST, teamsRequestSaga);
}

function* createTeamSaga({ payload, callback }) {
  const timestamp = Timestamp.now();
  const user = yield select(selectors.user);
  const newTeam = {
    ...payload,
    createdAt: timestamp,
    owner: user.uid,
    isDisabled: false,
  };

  try {
    const { id } = yield addDoc(collection(db, 'teams'), newTeam);
    yield put(teamsSuccess({ ...newTeam, id }));
    callback({ type: 'success' });
  } catch (error) {
    callback({ type: 'error' });
  }
}

export function* watchUpdateProjectShared() {
  yield takeLatest(actions.CREATE_TEAM, createTeamSaga);
}

function* updateTeamSaga({ payload, callback }) {
  const allTeams = yield select(selectors.allTeams);
  const user = yield select(selectors.user);
  const { users, activeTeam, ...rest } = payload;

  if (typeof activeTeam !== 'string') return;

  try {
    yield put(
      teamsSuccess([
        ...allTeams.filter(({ id }) => id !== activeTeam),
        { ...allTeams.find(({ id }) => id === activeTeam), ...users, ...rest },
      ]),
    );

    /* eslint-disable indent */
    yield updateDoc(doc(db, 'teams', activeTeam), {
      ...(typeof users !== 'undefined'
        ? {
            users: users.map(({ uid, email, access }) => ({
              access,
              ...(uid ? { uid } : { email }),
            })),
          }
        : {}),
      ...rest,
    });
    /* eslint-enable indent */

    const match = allTeams.find(({ id }) => id === activeTeam);

    const newUsers = users
      .filter(({ isNewUser }) => isNewUser)
      .filter(({ email }) => user.email !== email);

    if (newUsers.length) {
      yield put(
        pushNotification({
          type: 'team-member-add',
          teamMembers: newUsers.filter(({ uid }) => uid),
          context: {
            teamID: activeTeam,
            teamName: match.name || payload.name,
          },
        }),
      );
      yield put(showSnackbar('New user added to your team.'));

      yield all(
        newUsers.map(({ email, access, uid }) =>
          axios.post(
            `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`,
            {
              from: `Team ruttl <support@ruttl.com>`,
              to: email,
              subject: `${user.displayName} has invited you to the team ${
                match.name || payload.name
              } on Ruttl`,
              template: 'team-invite',
              templateVariables: JSON.stringify({
                teamAdmin: user.displayName,
                teamName: match.name || payload.name,
                access: `${access.charAt(0).toUpperCase()}${access.slice(1)}`,
                url: `${window.location.origin}${
                  uid ? `/team/${activeTeam}` : '/signup'
                }`,
              }),
            },
          ),
        ),
      );
    }

    const existingUsers = users
      .filter(({ isNewUser }) => !isNewUser)
      .filter(({ email }) => user.email !== email);

    if (existingUsers.length) {
      const prepare = (items) =>
        items
          .map(({ email, uid, access }) =>
            JSON.stringify({ email, uid, access }),
          )
          .sort();

      const diff = differenceBy(
        prepare(existingUsers),
        prepare(match?.users ?? []),
      ).map(JSON.parse);

      if (diff.length) {
        yield put(
          pushNotification({
            type: 'team-member-access',
            teamMembers: diff.filter(({ uid }) => uid),
            context: {
              teamID: activeTeam,
              teamName: match.name || payload.name,
            },
          }),
        );

        yield all(
          diff.map(({ email, access }) =>
            axios.post(
              `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/sendEmail`,
              {
                from: `Team ruttl <support@ruttl.com>`,
                to: email,
                subject: `You are now a ${access
                  .charAt(0)
                  .toUpperCase()}${access.slice(1)} in the team ${
                  match.name || payload.name
                } on Ruttl`,
                template: 'team-access',
                templateVariables: JSON.stringify({
                  teamName: match.name || payload.name,
                  access: `${access.charAt(0).toUpperCase()}${access.slice(1)}`,
                  url: `${window.location.origin}/team/${activeTeam}`,
                }),
              },
            ),
          ),
        );

        yield put(showSnackbar('Team users updated.'));
      }
    }

    callback({ type: 'success' });
    yield delay(7000);
    yield put(hideSnackbar());
  } catch (error) {
    callback({ type: 'error', payload: error.toString() });
  }
}

export function* watchUpdateTeam() {
  yield takeLatest(actions.UPDATE_TEAM, updateTeamSaga);
}

function* removeFromTeamSaga({ payload }) {
  if (!payload) return;

  const allTeams = yield select(selectors.allTeams);
  const team = allTeams.find((t) => t.id === payload);
  const user = yield select(selectors.user);

  try {
    yield updateDoc(doc(db, 'teams', payload), {
      users: team.users
        .filter(({ uid }) => uid !== user.uid)
        .map(({ uid, access }) => ({ uid, access })),
    });
    yield put(showSnackbar('You left the team.'));

    yield put(
      pushNotification({
        type: 'team-member-left',
        teamMembers: [team.owner],
        context: {
          teamID: team.id,
          teamName: team.name,
        },
      }),
    );

    yield delay(7000);
    yield put(hideSnackbar());
  } catch (error) {
    //
  }
}

export function* watchRemoveFromTeam() {
  yield takeLatest(actions.REMOVE_FROM_TEAM, removeFromTeamSaga);
}

function* updateThemeWorkerSaga({ payload, callback }) {
  const team = yield select(selectors.team);
  const teams = yield select(selectors.allTeams);

  const {
    customBranding,
    theme: { primaryColor, secondaryColor, textHighlightColor, logo, icon },
  } = payload || { customBranding: false, theme: {} };

  if (customBranding) {
    const updateObj = {
      logoURL: '/assets/img/logo.svg',
      iconURL: '/assets/img/icon.svg',
      ...team.theme,
      primaryColor,
      secondaryColor,
      textHighlightColor,
    };

    if (logo) {
      const snapshot = yield uploadBytes(
        ref(storage, `assets/${team.id}/${Date.now()}`),
        logo,
        {
          contentType: logo.type,
          cacheControl: 'private,max-age=31536000',
        },
      );

      const downloadURL = yield getDownloadURL(snapshot.ref);

      updateObj.logoURL = convertDownloadURLToGS(downloadURL);
    }

    if (icon) {
      const snapshot = yield uploadBytes(
        ref(storage, `assets/${team.id}/${Date.now()}`),
        icon,
        {
          contentType: icon.type,
          cacheControl: 'private,max-age=31536000',
        },
      );

      const downloadURL = yield getDownloadURL(snapshot.ref);

      updateObj.iconURL = convertDownloadURLToGS(downloadURL);
    }

    yield updateDoc(doc(db, 'teams', team.id), {
      customBranding,
      theme: updateObj,
    });

    yield put(
      teamsSuccess(
        teams.map((t) =>
          t.id === team.id ? { ...team, customBranding, theme: updateObj } : t,
        ),
      ),
    );

    yield put(showSnackbar('Custom branding is enabled.'));
  } else {
    yield updateDoc(doc(db, 'teams', team.id), { customBranding });

    yield put(
      teamsSuccess(
        teams.map((t) => (t.id === team.id ? { ...team, customBranding } : t)),
      ),
    );

    yield put(showSnackbar('Custom branding is disabled.'));
  }

  callback();

  yield delay(5000);

  yield put(hideSnackbar());
}

export function* updateThemeWatcherSaga() {
  yield takeLatest(actions.UPDATE_THEME, updateThemeWorkerSaga);
}

function* updateTeamNameWorkerSaga({ payload }) {
  const team = yield select(selectors.team);
  const teams = yield select(selectors.allTeams);

  yield updateDoc(doc(db, 'teams', team.id), {
    name: payload,
  });

  yield put(
    teamsSuccess(
      teams.map((t) => (t.id === team.id ? { ...team, name: payload } : t)),
    ),
  );

  yield put(showSnackbar('Team name is updated.'));
}

export function* updateTeamNameWatcherSaga() {
  yield takeLatest(actions.UPDATE_TEAM_NAME, updateTeamNameWorkerSaga);
}

function* updateCustomDomainSaga({ payload, callback }) {
  const team = yield select(selectors.team);
  const teams = yield select(selectors.allTeams);

  yield updateDoc(doc(db, 'teams', team.id), {
    domain: payload,
  });

  yield put(
    teamsSuccess(
      teams.map((t) => (t.id === team.id ? { ...team, domain: payload } : t)),
    ),
  );

  yield call(
    axios.get,
    `${process.env.REACT_APP_FIREBASE_FUNCTION_HOST}/syncCustomDomains`,
  );

  yield put(showSnackbar('Custom domain updated.'));

  callback();

  yield delay(7000);
  yield put(hideSnackbar());
}

export function* updateCustomDomainWatcherSaga() {
  yield takeLatest(actions.UPDATE_CUSTOM_DOMAIN, updateCustomDomainSaga);
}
